import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import AuthPageShell from "../components/AuthPageShell";
import EmailOtpForm from "../components/EmailOtpForm";
import { useSendEmailOtp } from "../../application/hooks/useSendEmailOtp";
import {
  AUTH_FLOW_PURPOSE_PASSWORD_RESET,
  AUTH_FLOW_PURPOSE_RESET_TOTP,
} from "../../domain/constants/authFlowConstants";
import { cn } from "@/lib/utils";
import { authTextButtonStyle } from "../theme/authTextButtonStyle";

const RESEND_COOLDOWN_SECONDS = 30;

const VerifyOtpPage = () => {
  const { t } = useTranslation("auth");
  const location = useLocation();
  const navigate = useNavigate();
  const { sendEmailOtp, loading: isResending } = useSendEmailOtp();
  const autoSendStarted = useRef(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);

  const email = location.state?.email || "your@example.com";
  const purpose = location.state?.purpose || "LOGIN";
  const autoSendOtp = Boolean(location.state?.autoSendOtp);
  const isLoginFlow = purpose === "LOGIN";

  const isResendDisabled = isResending || resendSecondsLeft > 0;

  useEffect(() => {
    if (!autoSendOtp || autoSendStarted.current) {
      return;
    }

    autoSendStarted.current = true;
    void sendEmailOtp(email, "LOGIN");
  }, [autoSendOtp, email, sendEmailOtp]);

  useEffect(() => {
    if (resendSecondsLeft <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setResendSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendSecondsLeft]);

  const handleResendCode = async () => {
    if (isResendDisabled) {
      return;
    }

    try {
      if (purpose === AUTH_FLOW_PURPOSE_PASSWORD_RESET) {
        await sendEmailOtp(email, AUTH_FLOW_PURPOSE_PASSWORD_RESET);
      } else if (purpose === AUTH_FLOW_PURPOSE_RESET_TOTP) {
        await sendEmailOtp(email, AUTH_FLOW_PURPOSE_RESET_TOTP);
      } else {
        await sendEmailOtp(email, "LOGIN");
      }

      setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
    } catch {
      // Keep resend available if the request fails.
    }
  };

  const resendLabel = isResending
    ? t("verifyOtp.sending")
    : resendSecondsLeft > 0
      ? t("verifyOtp.resendIn", { seconds: resendSecondsLeft })
      : t("verifyOtp.resend");

  const handleTryAnotherMethod = () => {
    navigate("/verification-code", { state: { email } });
  };

  return (
    <AuthPageShell
      title={t("verifyOtp.title")}
      subtitle={
        <>
          {t("verifyOtp.subtitle")}{" "}
          <span className="font-medium text-gray-700 break-all">{email}</span>
        </>
      }
    >
      <EmailOtpForm purpose={purpose} email={email} />

      <div className="mt-6 text-center text-sm text-gray-600">
        {t("verifyOtp.noCode")}{" "}
        <button
          type="button"
          onClick={() => void handleResendCode()}
          disabled={isResendDisabled}
          aria-disabled={isResendDisabled}
          className={cn(
            "transition",
            isResendDisabled
              ? "cursor-not-allowed text-gray-400"
              : "text-primary",
          )}
          style={authTextButtonStyle}
        >
          {resendLabel}
        </button>
      </div>

      {isLoginFlow && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={handleTryAnotherMethod}
            className="text-primary transition"
            style={authTextButtonStyle}
          >
            {t("verifyOtp.tryAnotherMethod")}
          </button>
        </div>
      )}
    </AuthPageShell>
  );
};

export default VerifyOtpPage;
